import katex from 'katex';
import { marked } from 'marked';
import 'katex/dist/katex.min.css';
import { sanitizeHtml } from './safeHtml';

type MathSegment = {
  token: string;
  expression: string;
  displayMode: boolean;
};

const LEGACY_LATEX_PATTERN = /@@LATEX\[([\s\S]*?)\]@@/g;
const LEGACY_MATH_PATTERN = /@@(?!LATEX\[)([\s\S]*?)@@/g;
const MATH_TOKEN_PREFIX = 'MATHSEGMENTTOKEN';
const CODE_TOKEN_PREFIX = 'CODESEGMENTTOKEN';
const HTML_CODE_TOKEN_PREFIX = 'HTMLCODESEGMENTTOKEN';

function normalizeMathSource(text: string): string {
  return text
    .replace(/@{3,}LATEX\[/g, '@@LATEX[')
    .replace(/\]@{3,}/g, ']@@')
    .replace(/\\\\n\\\\n/g, '\n\n')
    .replace(/\\\\n/g, '\n')
    .replace(/\\n\\n/g, '\n\n')
    .replace(/\\n/g, '\n')
    .replace(/\\{2}\(/g, '\\(')
    .replace(/\\{2}\)/g, '\\)')
    .replace(/\\{2}\[/g, '\\[')
    .replace(/\\{2}\]/g, '\\]');
}

function createToken(prefix: string, index: number): string {
  return `${prefix}${index}TOKEN`;
}

function protectSegments(source: string, pattern: RegExp, tokenPrefix: string): { text: string; tokens: string[] } {
  const tokens: string[] = [];
  const text = source.replace(pattern, (match: string) => {
    const token = createToken(tokenPrefix, tokens.length);
    tokens.push(match);
    return token;
  });
  return { text, tokens };
}

function restoreTokens(source: string, tokens: string[], tokenPrefix: string): string {
  let restored = source;
  for (let i = 0; i < tokens.length; i++) {
    restored = restored.split(createToken(tokenPrefix, i)).join(tokens[i]);
  }
  return restored;
}

function protectMarkdownCodeSegments(source: string): { text: string; tokens: string[] } {
  return protectSegments(source, /```[\s\S]*?```|`[^`\n]+`/g, CODE_TOKEN_PREFIX);
}

function protectHtmlCodeSegments(source: string): { text: string; tokens: string[] } {
  return protectSegments(source, /<pre[\s\S]*?<\/pre>|<code[\s\S]*?<\/code>/g, HTML_CODE_TOKEN_PREFIX);
}

function looksLikeMathExpression(expression: string): boolean {
  const trimmed = expression.trim();
  if (!trimmed) return false;
  if (/^[a-zA-Z]$/.test(trimmed)) return true;
  if (/^[\d\s.,]+$/.test(trimmed)) return false;
  return /\\[a-zA-Z]+|[=^_<>]|(?:\d\s*[+\-*/]\s*\d)|(?:sin|cos|tan|log|ln)\b|π|√|→|←/i.test(trimmed);
}

function normalizeLatexExpression(expression: string): string {
  let normalized = expression
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/\\u2011/g, '-')
    .replace(/\\u2019/g, "'")
    .replace(/\\u201c/g, '"')
    .replace(/\\u201d/g, '"')
    .trim();

  if (normalized.includes('\\\\')) {
    normalized = normalized.replace(/\\\\/g, '\\');
  }
  return normalized;
}

function extractMathSegments(source: string): { text: string; segments: MathSegment[] } {
  let text = source;
  const segments: MathSegment[] = [];

  const pushMathToken = (rawExpression: string, displayMode: boolean): string | null => {
    const expression = normalizeLatexExpression(rawExpression);
    if (!expression) return null;
    const token = createToken(MATH_TOKEN_PREFIX, segments.length);
    segments.push({ token, expression, displayMode });
    return token;
  };

  const replaceWithToken = (
    pattern: RegExp,
    displayMode: boolean,
    guard?: (expression: string) => boolean,
  ) => {
    text = text.replace(pattern, (match: string, expression: string) => {
      if (guard && !guard(expression)) {
        return match;
      }
      const token = pushMathToken(expression, displayMode);
      return token || match;
    });
  };

  replaceWithToken(LEGACY_LATEX_PATTERN, false);
  replaceWithToken(LEGACY_MATH_PATTERN, false);
  replaceWithToken(/\\\[([\s\S]+?)\\\]/g, true);
  replaceWithToken(/\$\$([\s\S]+?)\$\$/g, true);
  replaceWithToken(/\\\(([\s\S]+?)\\\)/g, false);

  text = text.replace(/(^|[^\\$])\$(?!\$)([^$\n]+?)\$(?!\$)/g, (match: string, prefix: string, expression: string) => {
    if (!looksLikeMathExpression(expression)) {
      return match;
    }
    const token = pushMathToken(expression, false);
    return token ? `${prefix}${token}` : match;
  });

  return { text, segments };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderMathSegment(segment: MathSegment): string {
  try {
    return katex.renderToString(segment.expression, {
      throwOnError: false,
      displayMode: segment.displayMode,
      output: 'html',
      trust: true,
      macros: {
        '\\N': '\\mathbb{N}',
        '\\R': '\\mathbb{R}',
        '\\Q': '\\mathbb{Q}',
        '\\Z': '\\mathbb{Z}',
      },
    });
  } catch (error: any) {
    console.warn('[renderMathInString] KaTeX rendering error:', error?.message, 'for expression:', segment.expression);
    return `<span class="math-fallback">${escapeHtml(segment.expression)}</span>`;
  }
}

function injectMathSegments(source: string, segments: MathSegment[]): string {
  let output = source;
  for (const segment of segments) {
    output = output.split(segment.token).join(renderMathSegment(segment));
  }
  return output;
}

function parseMarkdownToHtml(content: string): string {
  if (typeof marked.parse === 'function') {
    const result = marked.parse(content, { breaks: true, gfm: true });
    return typeof result === 'string' ? result : '';
  }
  return marked(content, { breaks: true, gfm: true }) as string;
}

/**
 * Enhanced math preprocessing with better LaTeX detection
 */
export function newEnhancedPreprocessMath(text: string): string {
  if (text === null || text === undefined) return '';
  let processedText = normalizeMathSource(String(text)).trim();
  if (!processedText) return '';

  processedText = processedText.replace(LEGACY_MATH_PATTERN, (match, expr) => {
    const trimmed = String(expr || '').trim();
    if (!trimmed) return match;
    return `@@LATEX[${trimmed}]@@`;
  });

  return processedText;
}

/**
 * Renders math expressions in HTML string using KaTeX
 */
export function renderMathInString(htmlString: string): string {
  if (!htmlString) return '';
  const normalized = normalizeMathSource(htmlString);
  const protectedCode = protectHtmlCodeSegments(normalized);
  const extracted = extractMathSegments(protectedCode.text);
  const rendered = injectMathSegments(extracted.text, extracted.segments);
  return restoreTokens(rendered, protectedCode.tokens, HTML_CODE_TOKEN_PREFIX);
}

export function processUnicodeEscapes(content: string): string {
  return content
    // Basic mathematical symbols
    .replace(/\\u2192/g, '→')   // Right arrow
    .replace(/\\u2190/g, '←')   // Left arrow  
    .replace(/\\u2191/g, '↑')   // Up arrow
    .replace(/\\u2193/g, '↓')   // Down arrow
    .replace(/\\u2194/g, '↔')   // Left-right arrow
    .replace(/\\u21d2/g, '⇒')   // Double right arrow (implies)
    .replace(/\\u21d0/g, '⇐')   // Double left arrow
    .replace(/\\u21d4/g, '⇔')   // Double left-right arrow (if and only if)
    .replace(/\\u03c0/g, 'π')   // Pi
    .replace(/\\u03b8/g, 'θ')   // Theta
    .replace(/\\u03b1/g, 'α')   // Alpha
    .replace(/\\u03b2/g, 'β')   // Beta
    .replace(/\\u03b3/g, 'γ')   // Gamma
    .replace(/\\u0394/g, 'Δ')   // Delta (uppercase)
    .replace(/\\u03b4/g, 'δ')   // Delta (lowercase)
    .replace(/\\u03b5/g, 'ε')   // Epsilon
    .replace(/\\u03bb/g, 'λ')   // Lambda
    .replace(/\\u03bc/g, 'μ')   // Mu
    .replace(/\\u03c3/g, 'σ')   // Sigma (lowercase)
    .replace(/\\u03a3/g, 'Σ')   // Sigma (uppercase - summation)
    .replace(/\\u03c4/g, 'τ')   // Tau
    .replace(/\\u03c6/g, 'φ')   // Phi
    .replace(/\\u03c9/g, 'ω')   // Omega (lowercase)
    .replace(/\\u03a9/g, 'Ω')   // Omega (uppercase)
    
    // Mathematical operators
    .replace(/\\u00d7/g, '×')   // Multiplication
    .replace(/\\u00f7/g, '÷')   // Division
    .replace(/\\u2208/g, '∈')   // Element of
    .replace(/\\u2209/g, '∉')   // Not element of
    .replace(/\\u2264/g, '≤')   // Less than or equal
    .replace(/\\u2265/g, '≥')   // Greater than or equal
    .replace(/\\u2260/g, '≠')   // Not equal
    .replace(/\\u2261/g, '≡')   // Identical to
    .replace(/\\u2248/g, '≈')   // Approximately equal
    .replace(/\\u00b1/g, '±')   // Plus-minus
    .replace(/\\u2213/g, '∓')   // Minus-plus
    .replace(/\\u221a/g, '√')   // Square root
    .replace(/\\u221b/g, '∛')   // Cube root
    .replace(/\\u221c/g, '∜')   // Fourth root
    .replace(/\\u221e/g, '∞')   // Infinity
    .replace(/\\u2202/g, '∂')   // Partial derivative
    .replace(/\\u222b/g, '∫')   // Integral
    .replace(/\\u222c/g, '∬')   // Double integral
    .replace(/\\u222d/g, '∭')   // Triple integral
    .replace(/\\u2211/g, '∑')   // N-ary summation
    .replace(/\\u220f/g, '∏')   // N-ary product
    .replace(/\\u2207/g, '∇')   // Nabla (gradient)
    
    // Set theory and logic
    .replace(/\\u2282/g, '⊂')   // Subset of
    .replace(/\\u2283/g, '⊃')   // Superset of
    .replace(/\\u2286/g, '⊆')   // Subset of or equal to
    .replace(/\\u2287/g, '⊇')   // Superset of or equal to
    .replace(/\\u2284/g, '⊄')   // Not a subset of
    .replace(/\\u2285/g, '⊅')   // Not a superset of
    .replace(/\\u2229/g, '∩')   // Intersection
    .replace(/\\u222a/g, '∪')   // Union
    .replace(/\\u2205/g, '∅')   // Empty set
    .replace(/\\u2200/g, '∀')   // For all
    .replace(/\\u2203/g, '∃')   // There exists
    .replace(/\\u2204/g, '∄')   // There does not exist
    .replace(/\\u2227/g, '∧')   // Logical and
    .replace(/\\u2228/g, '∨')   // Logical or
    .replace(/\\u00ac/g, '¬')   // Logical not
    
    // Additional symbols
    .replace(/\\u00b0/g, '°')   // Degree symbol
    .replace(/\\u00bd/g, '½')   // One half
    .replace(/\\u00bc/g, '¼')   // One quarter
    .replace(/\\u00be/g, '¾')   // Three quarters
    
    // Punctuation and typography
    .replace(/\\u2013/g, '–')   // En dash
    .replace(/\\u2014/g, '—')   // Em dash
    .replace(/\\u2018/g, '\u2018')   // Left single quote
    .replace(/\\u2019/g, '\u2019')   // Right single quote / apostrophe
    .replace(/\\u201c/g, '\u201c')   // Left double quote
    .replace(/\\u201d/g, '\u201d')   // Right double quote
    .replace(/\\u2011/g, '\u2011')
    .replace(/\\u2026/g, '…')   // Ellipsis
    .replace(/\\u00b7/g, '·')   // Middle dot
    .replace(/\\u2022/g, '•')   // Bullet point
    
    // Space characters
    .replace(/\\u202f/g, ' ')   // Narrow no-break space → regular space
    .replace(/\\u00a0/g, ' ')   // Non-breaking space → regular space  
    .replace(/\\u2009/g, ' ')   // Thin space → regular space
    .replace(/\\u200a/g, '')    // Hair space → empty (very thin, not needed)
    .replace(/\\u200b/g, '')    // Zero-width space → empty
    .replace(/\\u200c/g, '')    // Zero-width non-joiner → empty
    .replace(/\\u200d/g, '')    // Zero-width joiner → empty
    .replace(/\\ufeff/g, '')    // BOM / zero-width no-break space → empty
    
    // Clean up corrupted unicode sequences (e.g., "200b711" from broken \u200b)
    .replace(/200b(\d+)/g, '$1')  // Fix corrupted zero-width space followed by numbers
    .replace(/200[a-f]/gi, '')    // Remove other corrupted 200x sequences
    
    // Additional math and currency
    .replace(/\\u00b2/g, '²')   // Superscript 2
    .replace(/\\u00b3/g, '³')   // Superscript 3
    .replace(/\\u00b9/g, '¹')   // Superscript 1
    .replace(/\\u207f/g, 'ⁿ')   // Superscript n
    .replace(/\\u20ac/g, '€')   // Euro sign
    .replace(/\\u00a3/g, '£')   // Pound sign
    .replace(/\\u00a5/g, '¥');  // Yen sign
}

/**
 * Fallback LaTeX to Unicode conversion for unsupported environments
 */
export function convertLatexToUnicodeFallback(latexExpression: string): string {
  const greekLetters: Record<string, string> = {
    'alpha': 'α', 'beta': 'β', 'gamma': 'γ', 'delta': 'δ', 'epsilon': 'ε',
    'zeta': 'ζ', 'eta': 'η', 'theta': 'θ', 'iota': 'ι', 'kappa': 'κ',
    'lambda': 'λ', 'mu': 'μ', 'nu': 'ν', 'xi': 'ξ', 'omicron': 'ο',
    'pi': 'π', 'rho': 'ρ', 'sigma': 'σ', 'tau': 'τ', 'upsilon': 'υ',
    'phi': 'φ', 'chi': 'χ', 'psi': 'ψ', 'omega': 'ω',
    'Gamma': 'Γ', 'Delta': 'Δ', 'Theta': 'Θ', 'Lambda': 'Λ', 'Xi': 'Ξ',
    'Pi': 'Π', 'Sigma': 'Σ', 'Upsilon': 'Υ', 'Phi': 'Φ', 'Psi': 'Ψ', 'Omega': 'Ω'
  };

  const symbols: Record<string, string> = {
    'infty': '∞', 'sum': '∑', 'prod': '∏', 'int': '∫',
    'partial': '∂', 'nabla': '∇', 'pm': '±', 'mp': '∓',
    'times': '×', 'div': '÷', 'neq': '≠', 'leq': '≤', 'geq': '≥',
    'approx': '≈', 'equiv': '≡', 'in': '∈', 'notin': '∉',
    'subset': '⊂', 'supset': '⊃', 'cap': '∩', 'cup': '∪',
    'emptyset': '∅', 'exists': '∃', 'forall': '∀', 'therefore': '∴',
    'because': '∵', 'angle': '∠', 'perp': '⊥', 'parallel': '∥'
  };

  let result = latexExpression;

  // Replace Greek letters
  Object.entries(greekLetters).forEach(([latex, unicode]) => {
    result = result.replace(new RegExp(`\\\\${latex}\\b`, 'g'), unicode);
  });

  // Replace symbols
  Object.entries(symbols).forEach(([latex, unicode]) => {
    result = result.replace(new RegExp(`\\\\${latex}\\b`, 'g'), unicode);
  });

  // Handle superscripts and subscripts (basic cases)
  result = result.replace(/\^{([^}]+)}/g, (match, content) => {
    const superscriptMap: Record<string, string> = {
      '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵',
      '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹', '+': '⁺', '-': '⁻',
      '=': '⁼', '(': '⁽', ')': '⁾', 'n': 'ⁿ'
    };
    return content.split('').map((char: string) => superscriptMap[char] || char).join('');
  });

  result = result.replace(/_{([^}]+)}/g, (match, content) => {
    const subscriptMap: Record<string, string> = {
      '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄', '5': '₅',
      '6': '₆', '7': '₇', '8': '₈', '9': '₉', '+': '₊', '-': '₋',
      '=': '₌', '(': '₍', ')': '₎'
    };
    return content.split('').map((char: string) => subscriptMap[char] || char).join('');
  });

  // Clean up remaining LaTeX commands
  result = result.replace(/\\[a-zA-Z]+/g, '');
  result = result.replace(/[{}]/g, '');

  return result;
}

/**
 * Creates both rich (KaTeX) and plain (Unicode fallback) versions of math content
 */
export function createCrossPlatformMath(content: string): { rich: string; plain: string } {
  const preprocessed = newEnhancedPreprocessMath(content);
  const markedResult = marked(preprocessed);
  const rich = sanitizeHtml(renderMathInString(typeof markedResult === 'string' ? markedResult : ''));
  
  // For plain version, convert LaTeX to Unicode fallback
  const plain = content.replace(/\$\$([^$]+)\$\$/g, (match, latex) => {
    return convertLatexToUnicodeFallback(latex);
  }).replace(/\$([^$]+)\$/g, (match, latex) => {
    return convertLatexToUnicodeFallback(latex);
  });

  return { rich, plain };
}

/**
 * Main function to render content with math support
 */
export function renderContent(content: string): string {
  if (!content) return '';
  
  try {
    let processed = processUnicodeEscapes(String(content));
    processed = newEnhancedPreprocessMath(processed);

    // Keep markdown code blocks untouched by math extraction.
    const protectedCode = protectMarkdownCodeSegments(processed);
    const extracted = extractMathSegments(protectedCode.text);
    const markdownSource = restoreTokens(extracted.text, protectedCode.tokens, CODE_TOKEN_PREFIX);
    const html = parseMarkdownToHtml(markdownSource);
    return sanitizeHtml(injectMathSegments(html, extracted.segments));
  } catch (error) {
    console.error('Error rendering content:', error);
    return sanitizeHtml(parseMarkdownToHtml(String(content)));
  }
}
